import { fetchConfig, freshCurve, quoteBuy, connection, maxDevBuyLamports } from "../chain.js";
import { launchToken } from "../actions.js";
import { walletKey, onWalletChange, connectWallet } from "../wallet.js";
import { $, esc, fmtSol, fmtTokens, fmtBps, parseUnits, runTx, toast } from "../ui.js";
import { describeError } from "../errors.js";

const MAX_IMG = 2 * 1024 * 1024;
const OK_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];
const RENT_ESTIMATE = 30_000_000n; // ~0.03 SOL: accounts rent + metadata + network fee (upper bound)
const byteLen = (s) => new TextEncoder().encode(s).length;

export function mountCreate(root) {
  root.innerHTML = `
  <section class="create">
    <div class="create-intro">
      <p class="eyebrow">Dig a new well</p>
      <h1 class="display-md">Launch your token</h1>
      <p class="lede">One transaction, one signature. Your token, its bonding curve and its well are created together,
      the full supply of 1,000,000,000 goes onto the curve and the mint authority is revoked for good.</p>
      <ul class="facts">
        <li><span class="fact-ico">◈</span><span>Your dev buy executes inside the launch transaction at the normal 4% fee, before anyone else can trade.</span></li>
        <li><span class="fact-ico">⏱</span><span>For the first 60 seconds after launch, trades pay a 15% fee that falls to 4%; the extra goes into the well.</span></li>
        <li><span class="fact-ico">💧</span><span>After that, every trade pays 4%: 1% to the platform and 3% into the well, which drains to staked holders gradually (about an hour time constant).</span></li>
        <li><span class="fact-ico">⚖</span><span>The dev buy is capped at 15% of the supply.</span></li>
        <li><span class="fact-ico">∞</span><span>No graduation, no migration: the token trades on its curve forever.</span></li>
      </ul>
    </div>

    <form class="panel create-form" id="create-form" novalidate>
      <div class="field-row">
        <label class="field">
          <span>Name <em>max 32 bytes</em></span>
          <input name="name" maxlength="32" required placeholder="Abyssal Frog" autocomplete="off" />
        </label>
        <label class="field field-sm">
          <span>Ticker <em>max 10</em></span>
          <input name="symbol" maxlength="10" required placeholder="FROG" autocomplete="off" class="upper" />
        </label>
      </div>

      <div class="field">
        <span>Image <em>PNG, JPG, GIF or WEBP · up to 2 MB</em></span>
        <label class="drop" id="drop" tabindex="0">
          <input type="file" name="image" accept="image/png,image/jpeg,image/gif,image/webp" hidden />
          <img id="preview" alt="" hidden />
          <span class="drop-hint" id="drop-hint">Drop an image here or <u>browse</u></span>
        </label>
      </div>

      <label class="field">
        <span>Description <em>optional</em></span>
        <textarea name="description" rows="3" maxlength="1000" placeholder="What lives at the bottom of this well?"></textarea>
      </label>

      <div class="field-row three">
        <label class="field"><span>X / Twitter</span><input name="twitter" placeholder="@handle" autocomplete="off" /></label>
        <label class="field"><span>Telegram</span><input name="telegram" placeholder="t.me/group" autocomplete="off" /></label>
        <label class="field"><span>Website</span><input name="website" placeholder="https://" autocomplete="off" /></label>
      </div>

      <div class="field">
        <span>Dev buy <em>SOL, optional, fee included</em></span>
        <div class="input-unit"><input name="devbuy" aria-label="Dev buy in SOL" inputmode="decimal" placeholder="0.0" autocomplete="off" /><b>SOL</b></div>
        <div class="chips">
          <button type="button" class="chip" data-dev="0">None</button>
          <button type="button" class="chip" data-dev="0.1">0.1</button>
          <button type="button" class="chip" data-dev="0.25">0.25</button>
          <button type="button" class="chip" data-dev="0.5">0.5</button>
          <button type="button" class="chip" data-dev="1">1</button>
          <button type="button" class="chip chip-max" id="dev-max">Max</button>
        </div>
        <div class="muted small" id="dev-cap">Max dev buy: 15% of the supply</div>
      </div>

      <div class="quote" id="quote" aria-live="polite"></div>

      <p class="notice"><b>Your dev buy goes straight to your wallet. No lock, no vesting — you can sell any time.</b><br/>
      For the first 60 seconds after launch, trades pay a 15% fee that falls to 4%; the extra goes into the well.</p>

      <button class="btn btn-primary btn-block" id="launch-btn" type="submit">Launch token</button>
      <p class="muted small" id="launch-note">Phantom will ask for one signature. Account rent and network fees are about 0.03 SOL on top of your dev buy.</p>
    </form>
  </section>`;

  const form = $("#create-form", root);
  const fileInput = form.querySelector('input[type="file"]');
  const drop = $("#drop", root);
  const preview = $("#preview", root);
  const hint = $("#drop-hint", root);
  const quoteEl = $("#quote", root);
  let file = null;
  let cfg = null;
  let devCap = null; // { lamports, capTokens }
  let solBalance = null;
  let lastUpload = null;
  let previewUrl = null;
  let alive = true;

  function setFile(f) {
    if (!f) return;
    if (!OK_TYPES.includes(f.type)) return toast({ kind: "error", title: "Image", body: "Use a PNG, JPG, GIF or WEBP image." });
    if (f.size > MAX_IMG) return toast({ kind: "error", title: "Image", body: "Image is too large (max 2 MB)." });
    file = f;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    previewUrl = URL.createObjectURL(f);
    preview.src = previewUrl;
    preview.hidden = false;
    hint.textContent = `${f.name} · ${(f.size / 1024).toFixed(0)} KB · click to change`;
    drop.classList.add("has-file");
  }
  fileInput.addEventListener("change", () => setFile(fileInput.files[0]));
  drop.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      fileInput.click();
    }
  });
  ["dragenter", "dragover"].forEach((t) =>
    drop.addEventListener(t, (e) => {
      e.preventDefault();
      drop.classList.add("over");
    })
  );
  ["dragleave", "drop"].forEach((t) =>
    drop.addEventListener(t, (e) => {
      e.preventDefault();
      drop.classList.remove("over");
    })
  );
  drop.addEventListener("drop", (e) => setFile(e.dataTransfer.files[0]));

  form.querySelectorAll("[data-dev]").forEach((b) =>
    b.addEventListener("click", () => {
      form.devbuy.value = b.dataset.dev === "0" ? "" : b.dataset.dev;
      renderQuote();
    })
  );
  form.symbol.addEventListener("input", () => (form.symbol.value = form.symbol.value.toUpperCase().replace(/\s/g, "")));
  form.devbuy.addEventListener("input", renderQuote);
  $("#dev-max", root).addEventListener("click", () => {
    if (!devCap) return;
    // round down to 4 decimals so the field stays readable and under the cap
    const lam = (devCap.lamports / 100_000n) * 100_000n;
    form.devbuy.value = (Number(lam) / 1e9).toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
    renderQuote();
  });

  function devLamports() {
    const v = form.devbuy.value.trim();
    if (!v) return 0n;
    return parseUnits(v, 9);
  }

  function renderQuote() {
    if (!cfg) {
      quoteEl.innerHTML = `<div class="quote-row muted">Loading platform settings…</div>`;
      return;
    }
    const lam = devLamports();
    if (lam === null) {
      quoteEl.innerHTML = `<div class="quote-row err">Enter a valid SOL amount.</div>`;
      return;
    }
    const curve = freshCurve(cfg);
    const rows = [];
    if (lam > 0n) {
      const q = quoteBuy(curve, lam, cfg.baseFeeBps, cfg.platformFeeBps);
      const pct = Number((q.tokens * 1_000_000n) / cfg.totalSupply) / 10_000;
      rows.push(`<div class="quote-big"><span class="mono">${fmtTokens(q.tokens, 2)}</span> tokens <em>${pct.toFixed(2)}% of supply</em></div>`);
      rows.push(`<div class="quote-row"><span>Dev buy fee (${fmtBps(cfg.baseFeeBps)})</span><span class="mono">${fmtSol(q.fee.total, 6)} SOL</span></div>`);
      rows.push(`<div class="quote-row sub"><span>↳ into the well</span><span class="mono cyan">${fmtSol(q.fee.water, 6)} SOL</span></div>`);
      rows.push(`<div class="quote-row sub"><span>↳ platform</span><span class="mono">${fmtSol(q.fee.platform, 6)} SOL</span></div>`);
      rows.push(`<div class="quote-row"><span>SOL onto the curve</span><span class="mono">${fmtSol(q.net, 6)} SOL</span></div>`);
      if (devCap && lam > devCap.lamports) rows.push(`<div class="quote-row err">Dev buy cannot exceed 15% of the supply (max ${fmtSol(devCap.lamports, 4)} SOL).</div>`);
    } else {
      rows.push(`<div class="quote-row muted">No dev buy. The curve opens with the anti-sniper fee for 60 seconds.</div>`);
    }
    if (solBalance !== null) {
      const need = lam + RENT_ESTIMATE;
      rows.push(
        `<div class="quote-row ${solBalance < need ? "err" : "muted"}"><span>Wallet balance</span><span class="mono">${fmtSol(solBalance, 4)} SOL</span></div>`
      );
    }
    if (cfg.launchesPaused) rows.push(`<div class="quote-row err">New launches are paused by the platform right now.</div>`);
    quoteEl.innerHTML = rows.join("");
  }

  async function refreshBalance() {
    const pk = walletKey();
    if (!pk) {
      solBalance = null;
      return renderQuote();
    }
    try {
      solBalance = BigInt(await connection.getBalance(pk, "confirmed"));
    } catch {
      solBalance = null;
    }
    if (alive) renderQuote();
  }

  fetchConfig(true)
    .then((c) => {
      cfg = c;
      if (c) {
        devCap = maxDevBuyLamports(c);
        $("#dev-cap", root).textContent = `Max dev buy: ${fmtSol(devCap.lamports, 4)} SOL (15% of the supply, ${fmtTokens(devCap.capTokens, 0)} tokens, ${fmtBps(c.baseFeeBps)} fee included)`;
      }
      if (!c) {
        quoteEl.innerHTML = `<div class="quote-row err">The platform is not initialized on this network yet.</div>`;
        return;
      }
      renderQuote();
    })
    .catch((e) => (quoteEl.innerHTML = `<div class="quote-row err">${esc(describeError(e))}</div>`));
  refreshBalance();
  const offWallet = onWalletChange(refreshBalance);

  function validate() {
    const name = form.name.value.trim();
    const symbol = form.symbol.value.trim().toUpperCase();
    if (!name) return "Enter a token name.";
    if (byteLen(name) > 32) return "Token name is too long (max 32 bytes).";
    if (!symbol) return "Enter a ticker.";
    if (byteLen(symbol) > 10) return "Ticker is too long (max 10 bytes).";
    if (!file) return "Add an image for your token.";
    const lam = devLamports();
    if (lam === null) return "Enter a valid dev buy amount.";
    if (!cfg) return "Platform settings are not loaded yet.";
    if (cfg.launchesPaused) return "New launches are paused by the platform right now.";
    if (lam > 0n) {
      const q = quoteBuy(freshCurve(cfg), lam, cfg.baseFeeBps, cfg.platformFeeBps);
      if (q.zero) return "Dev buy is too small to buy any tokens.";
      if (q.exceeds || (devCap && (lam > devCap.lamports || q.tokens > devCap.capTokens))) return "Dev buy cannot exceed 15% of the supply.";
    }
    if (solBalance !== null && solBalance < lam + RENT_ESTIMATE / 2n) return "Not enough SOL in your wallet for this launch.";
    return null;
  }

  async function upload() {
    const fields = ["name", "symbol", "description", "twitter", "telegram", "website"];
    const key = fields.map((f) => form[f].value.trim()).join("|") + `|${file.name}|${file.size}|${file.lastModified}`;
    if (lastUpload?.key === key) return lastUpload.uri;
    const fd = new FormData();
    for (const f of fields) fd.append(f, f === "symbol" ? form[f].value.trim().toUpperCase() : form[f].value.trim());
    fd.append("image", file);
    const r = await fetch("/api/metadata", { method: "POST", body: fd });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || `Metadata upload failed (${r.status}).`);
    if (byteLen(j.uri) > 200) throw new Error("Metadata link is too long. Ask the operator to use a shorter PUBLIC_URL.");
    lastUpload = { key, uri: j.uri };
    return j.uri;
  }

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    if (!walletKey()) {
      try {
        await connectWallet();
        await refreshBalance();
      } catch (err) {
        return toast({ kind: "error", title: "Wallet", body: describeError(err) });
      }
    }
    const problem = validate();
    if (problem) return toast({ kind: "error", title: "Check the form", body: problem });
    const btn = $("#launch-btn", root);
    const lam = devLamports();
    const q = lam > 0n ? quoteBuy(freshCurve(cfg), lam, cfg.baseFeeBps, cfg.platformFeeBps) : null;
    const minOut = q ? (q.tokens * 99n) / 100n : 0n;
    const res = await runTx(btn, "Launch", async (stage) => {
      stage("preparing");
      btn.innerHTML = `<span class="spin"></span>Uploading metadata…`;
      const uri = await upload();
      return launchToken({
        name: form.name.value.trim(),
        symbol: form.symbol.value.trim().toUpperCase(),
        uri,
        devBuyLamports: lam,
        devMinTokensOut: minOut,
        onStage: stage,
      });
    });
    if (res?.mint) location.hash = `#/token/${res.mint.toBase58()}`;
  });

  return () => {
    alive = false;
    offWallet();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  };
}
